<template>
  <component
    :is="flat ? 'div' : ClientOverviewCard"
    v-if="visible"
    v-bind="flat ? {} : cardProps"
    data-test="client-custom-fields"
  >
    <WdsStack direction-column gap-md>
      <WdsText v-if="flat" :sub-text="$t('Custom fields')" />
      <LoadingList v-if="loading" :count="2" no-bg data-test="client-custom-fields-loading" />
      <WdsText v-else-if="loadFailed" error data-test="client-custom-fields-load-error">
        {{ $t('An error occurred') }}
      </WdsText>
      <template v-else>
        <FormCustomFields
          :fields="customFields.fields"
          :field-values="customFields.localFieldValues"
          :field-value-fragments="customFields.initialFieldValueFragments"
          :errors="customFields.fieldErrors"
          :disable-inputs="customFields.saving"
          @update:field-values="onEdit"
        />
        <WdsStack
          align-center
          gap-sm
          :class="{ 'client-custom-fields__actions--sticky': flat }"
          data-test="client-custom-fields-actions"
        >
          <WdsButton
            primary
            sm
            :loading="customFields.saving"
            :disabled="!customFields.hasUnsavedChanges || customFields.saving"
            data-test="client-custom-fields-save"
            @click="onSave"
          >
            {{ $t('Save') }}
          </WdsButton>
          <WdsText v-if="saved" secondary sm data-test="client-custom-fields-saved">
            {{ $t('Saved') }}
          </WdsText>
          <WdsText v-if="failed" error sm data-test="client-custom-fields-error">
            {{ $t('An error occurred') }}
          </WdsText>
        </WdsStack>
      </template>
    </WdsStack>
  </component>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsButton, WdsStack, WdsText } from '@worksome/wds-components'
import { graphql, useFragment } from '@/gql'
import { CustomFieldTypeValueInput, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import ClientOverviewCard from '@/components/Clients/OverviewCards/ClientOverviewCard.vue'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import FormCustomFields from '@/components/Fieldable/FormCustomFields.vue'
import useProfileModalCustomFields from '@/components/Fieldable/composables/useProfileModalCustomFields'
import useNavigateWarning from '@/composables/useNavigateWarning'

const props = defineProps<{
  companySupplierId: string
  /** Drawer rendering: the surrounding sections there carry no card chrome. */
  flat?: boolean
}>()

const ClientCustomFieldsFragment = graphql(`
  fragment ClientCustomFields on CompanySupplier {
    id
    supplier {
      id
    }
    customFieldValues {
      ...FieldableFormCustomFieldValue
    }
  }
`)

const {
  result,
  loading: clientLoading,
  error: clientError,
} = useQuery(
  graphql(`
    query ClientCustomFieldsData($id: ID!) {
      clientRelation(id: $id) {
        ...ClientCustomFields
      }
    }
  `),
  () => ({ id: props.companySupplierId }),
  { clientId: 'publicApi' },
)

const client = computed(() =>
  result.value?.clientRelation?.__typename === 'CompanySupplier'
    ? (useFragment(ClientCustomFieldsFragment, result.value.clientRelation) ?? null)
    : null,
)

// The supplier owns these definitions, so the client side never gets an editor.
const viewerIsSupplier = computed(() => client.value?.supplier?.id === currentAccount.globalId)

const customFieldValues = computed(() => client.value?.customFieldValues ?? [])

const customFields = reactive(
  useProfileModalCustomFields(
    currentAccount.globalId,
    props.companySupplierId,
    TypeSupportingCustomFieldValues.CompanySupplier,
    viewerIsSupplier,
    customFieldValues,
  ),
)

useNavigateWarning(computed(() => customFields.hasUnsavedChanges))

// The definitions only load once the supplier check resolves, so both count as loading.
const loading = computed(() => clientLoading.value || customFields.fieldsLoading)

// A failed query must not read as "no fields defined".
const loadFailed = computed(() => !!clientError.value || !!customFields.fieldsError)

// Nothing defined means nothing to show, but the card has to stay up while they load.
const visible = computed(
  () =>
    clientLoading.value ||
    loadFailed.value ||
    (viewerIsSupplier.value && customFields.showCustomFieldsSection),
)

const cardProps = computed(() => ({
  title: $t('Custom fields'),
  loading: loading.value,
  loadingTiles: 0,
}))

const saved = ref(false)
const failed = ref(false)

function onEdit(values: CustomFieldTypeValueInput[]) {
  customFields.localFieldValues = values
  saved.value = false
  failed.value = false
}

async function onSave() {
  const ok = await customFields.handleSave()
  saved.value = ok
  // Field errors already explain a failed required check; the generic one is for the request.
  failed.value = !ok && Object.keys(customFields.fieldErrors).length === 0
}
</script>

<style scoped lang="scss">
// Drawer only: on the full page, body.loggedin's overflow pins sticky content to the body.
.client-custom-fields__actions--sticky {
  @apply tw-sticky tw-bottom-0 tw-bg-white tw-py-2;
}
</style>
